<template>
  <div class="sub-header">
    <div class="d-flex justify-content-start">
      <SubHeaderButton v-if="can('view', 'shipments')" :to="shipmentsPage" icon="truck" name="Shipments" />
      <SubHeaderButton
        v-if="can('view', 'assignmentsList')"
        :to="assignmentsList"
        :add-to="createAssignmentsPage"
        name="Assignments"
        icon="file-document-multiple-outline"
      />
      <SubHeaderButton
        v-if="can('manage', 'customer')"
        :to="customersArea"
        :add-to="createCustomerPage"
        icon="account-outline"
        name="Customers"
      />
      <SubHeaderButton
        v-if="can('view', 'statistics')"
        :to="statistics"
        icon="file-document-outline"
        name="Statistics"
      />
      <SubHeaderButton v-if="can('view', 'pickups-list')" :to="pickupsList" icon="magnify" name="Pickups" />
      <SubHeaderButton v-if="can('view', 'faq')" :to="faqPage" :add-to="createFaqPage" icon="help" name="FAQ" />
      <SubHeaderButton v-if="can('view', 'scan')" :to="scanPage" icon="file-document-multiple-outline" name="Scan" />
      <SubHeaderButton
        v-if="can('manage', 'proxy')"
        :to="proxyMessagesPage"
        icon="file-document-multiple-outline"
        name="Proxy"
      />
      <SubHeaderButton v-if="can('manage', 'proxy')" :to="messagingProtectionPage" icon="shield-check-outline" name="Messaging Protection" />
      <SubHeaderButton v-if="can('view', 'profile')" :to="profilePage" icon="account" name="Profile" />
      <SubHeaderButton v-if="can('view', 'addressBook')" :to="addressBookPage" icon="book-account-outline" name="Address Book" />
    </div>
  </div>
</template>

<script lang="ts">
import type { RouterLink } from '@/types/Components/Router'
import { defineComponent, computed, type ComputedRef } from 'vue'
import { useAccountStore } from '@/store/account'
import {
  CUSTOMER_ASSIGNMENTS_CREATE,
  CUSTOMER_ASSIGNMENTS_LIST,
  STAFF_ASSIGNMENTS_CREATE,
  STAFF_ASSIGNMENTS_LIST,
  STAFF_BASE_PRICE_PAGE,
  STAFF_CUSTOMERS_CREATE,
  STAFF_CUSTOMERS_LIST,
  STAFF_STATISTICS,
  FAQ_CREATE,
  FAQ_LIST,
  CUSTOMER_STATISTICS,
  STAFF_PICKUPS_LIST,
  STAFF_SHIPMENTS,
  SCAN_PAGE,
  CUSTOMER_SHIPMENTS,
  CUSTOMER_PROFILE,
  PROXY_MESSAGES,
  STAFF_MESSAGING_PROTECTION,
  CUSTOMER_ADDRESS_BOOK,
} from '@/types/router'
import SubHeaderButton from '@/views/Components/PageParts/SubHeaderButton.vue'

export default defineComponent({
  name: 'SubHeader',
  components: { SubHeaderButton },
  setup() {
    const accountStore = useAccountStore()

    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)
    const ability: ComputedRef = computed(() => accountStore.ability)

    const assignmentsList: ComputedRef<RouterLink> = computed(() => ({
      name: isStaff.value ? STAFF_ASSIGNMENTS_LIST : CUSTOMER_ASSIGNMENTS_LIST,
    }))
    const createAssignmentsPage: ComputedRef<RouterLink> = computed(() => ({
      name: isStaff.value ? STAFF_ASSIGNMENTS_CREATE : CUSTOMER_ASSIGNMENTS_CREATE,
    }))
    const statistics: ComputedRef<RouterLink> = computed(() => ({
      name: isStaff.value ? STAFF_STATISTICS : CUSTOMER_STATISTICS,
    }))
    const shipmentsPage: ComputedRef<RouterLink> = computed(() => ({
      name: isStaff.value ? STAFF_SHIPMENTS : CUSTOMER_SHIPMENTS,
    }))
    const createFaqPage: ComputedRef<RouterLink | null> = computed(() => (isStaff.value ? { name: FAQ_CREATE } : null))

    const scanPage: RouterLink = { name: SCAN_PAGE }
    const proxyMessagesPage: RouterLink = { name: PROXY_MESSAGES }
    const profilePage: RouterLink = { name: CUSTOMER_PROFILE }
    const customersArea: RouterLink = { name: STAFF_CUSTOMERS_LIST }
    const createCustomerPage: RouterLink = { name: STAFF_CUSTOMERS_CREATE }
    const pickupsList: RouterLink = { name: STAFF_PICKUPS_LIST }
    const basePricePage: RouterLink = { name: STAFF_BASE_PRICE_PAGE }
    const faqPage: RouterLink = { name: FAQ_LIST }
    const addressBookPage: RouterLink = { name: CUSTOMER_ADDRESS_BOOK }

    return {
      messagingProtectionPage: { name: STAFF_MESSAGING_PROTECTION },
      faqPage,
      isStaff,
      addressBookPage,
      scanPage,
      proxyMessagesPage,
      statistics,
      pickupsList,
      profilePage,
      basePricePage,
      createFaqPage,
      customersArea,
      shipmentsPage,
      assignmentsList,
      createCustomerPage,
      createAssignmentsPage,
      can: (rule: string, section: string) => ability.value.can(rule, section),
    }
  },
})
</script>
